"use client";

import { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  Stack,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  QuestionAnswer,
  Send,
  Mic,
  Stop,
  HourglassEmpty,
  Timer,
} from "@mui/icons-material";
import axios from "axios";

const TRANSCRIPTION_API_URL = // "http://127.0.0.1:8000/api/transcribe-audio/";
  "https://tdtlworld.com/dss-gatep-backend/api/transcribe-audio/";

// --- HELPER FUNCTIONS FOR WAV ENCODING ---
const flattenArray = (buffers) => {
  const length = buffers.reduce((acc, curr) => acc + curr.length, 0);
  const result = new Float32Array(length);
  let offset = 0;
  buffers.forEach((b) => {
    result.set(b, offset);
    offset += b.length;
  });
  return result;
};

const writeString = (view, offset, string) => {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
};

const floatTo16BitPCM = (output, offset, input) => {
  for (let i = 0; i < input.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, input[i]));
    output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
};

const writeWAVHeader = (view, sampleRate, numSamples) => {
  const blockAlign = 1 * 2;
  const byteRate = sampleRate * blockAlign;
  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + numSamples * 2, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, "data");
  view.setUint32(40, numSamples * 2, true);
};

const encodeWAV = (buffers, sampleRate) => {
  const flatBuffer = flattenArray(buffers);
  const buffer = new ArrayBuffer(44 + flatBuffer.length * 2);
  const view = new DataView(buffer);
  writeWAVHeader(view, sampleRate, flatBuffer.length);
  floatTo16BitPCM(view, 44, flatBuffer);
  return new Blob([view], { type: "audio/wav" });
};

// --- MAIN COMPONENT ---
const QuestionPanel = ({
  questionText,
  roundName,
  questionNumber,
  onSubmit,
  isSubmitting,
  userMicEnabled,
  onTranscribingChange,
  onAnswerChange,
  answeringTimeRemaining, // Removed thinkingTimeRemaining
  isQuestionPanelTranscribing,
  isSpeaking,
}) => {
  const [answerState, setAnswerState] = useState("");
  const [error, setError] = useState("");
  const [copyPasteAlert, setCopyPasteAlert] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [micError, setMicError] = useState("");

  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const scriptProcessorRef = useRef(null);
  const mediaStreamSourceRef = useRef(null);
  const audioBufferRef = useRef([]);

  const setAnswer = (valueOrFn) => {
    if (typeof valueOrFn === "function") {
      // It's an updater function from transcription
      setAnswerState((prev) => {
        const newValue = valueOrFn(prev);
        if (onAnswerChange) {
          onAnswerChange(newValue);
        }
        return newValue;
      });
    } else {
      // It's a direct value from the text field
      setAnswerState(valueOrFn);
      if (onAnswerChange) {
        onAnswerChange(valueOrFn);
      }
    }
  };

  const handleSubmit = () => {
    if (isRecording) {
      setError("Please stop the recording before submitting.");
      return;
    }
    if (!answerState.trim()) {
      setError("Please provide an answer before submitting.");
      return;
    }
    setError("");
    onSubmit(answerState);
    setAnswer("");
  };

  const preventAction = (e) => {
    e.preventDefault();
    setCopyPasteAlert(true);
    setTimeout(() => setCopyPasteAlert(false), 3000);
  };

  const handleTranscription = async (wavBlob) => {
    setIsTranscribing(true);
    if (onTranscribingChange) onTranscribingChange(true);
    setError("");
    setMicError("");

    const formData = new FormData();
    formData.append("audio", wavBlob, "recording.wav");

    try {
      const response = await axios.post(TRANSCRIPTION_API_URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const transcribedText = response.data.transcription;
      if (transcribedText) {
        setAnswer(
          (prev) => prev.trim() + (prev.trim() ? " " : "") + transcribedText
        );
      }
    } catch (err) {
      console.error("Transcription API error:", err);
      setError(
        "Could not transcribe audio. Please try again or type your answer."
      );
    } finally {
      setIsTranscribing(false);
      if (onTranscribingChange) onTranscribingChange(false);
    }
  };

  const startRecording = async () => {
    setMicError("");
    if (!userMicEnabled) {
      setMicError("Microphone is currently disabled.");
      return;
    }
    try {
      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      mediaStreamSourceRef.current =
        audioContextRef.current.createMediaStreamSource(mediaStreamRef.current);
      scriptProcessorRef.current =
        audioContextRef.current.createScriptProcessor(4096, 1, 1);
      audioBufferRef.current = [];

      scriptProcessorRef.current.onaudioprocess = (e) => {
        audioBufferRef.current.push(
          new Float32Array(e.inputBuffer.getChannelData(0))
        );
      };

      mediaStreamSourceRef.current.connect(scriptProcessorRef.current);
      scriptProcessorRef.current.connect(audioContextRef.current.destination);
      setIsRecording(true);
    } catch (err) {
      console.error("Microphone access error:", err);
      setMicError(
        "Microphone access denied. Please allow permissions in your browser settings."
      );
    }
  };

  const stopRecording = () => {
    if (!isRecording) return;

    // Update the UI state immediately
    setIsRecording(false);

    // Perform background tasks
    try {
      scriptProcessorRef.current.disconnect();
      mediaStreamSourceRef.current.disconnect();
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());

      const wavBlob = encodeWAV(
        audioBufferRef.current,
        audioContextRef.current.sampleRate
      );

      // Start transcription process
      handleTranscription(wavBlob);
    } catch (e) {
      console.error("Error stopping recording:", e);
      setError("An error occurred while stopping the recording.");
      // Ensure transcription state is also reset if it was set
      setIsTranscribing(false);
      if (onTranscribingChange) onTranscribingChange(false);
    }
  };

  const handleMicClick = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  useEffect(() => {
    if (!userMicEnabled && isRecording) {
      stopRecording();
    }
    return () => {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (scriptProcessorRef.current) {
        scriptProcessorRef.current.disconnect();
      }
    };
  }, [userMicEnabled]);

  const isDisabled = isSubmitting || isTranscribing || isRecording;
  const micButtonDisabled = isSubmitting || isTranscribing || !userMicEnabled;

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const getTimerColor = (timeRemaining) => {
    return timeRemaining <= 15 ? "#D92626" : "#4F46BA";
  };

  const getCurrentTimer = () => {
    // Don't show timer when interviewer is speaking
    if (isSpeaking) {
      return null;
    }

    // Only show answering timer (removed thinking timer)
    if (answeringTimeRemaining > 0) {
      return {
        time: answeringTimeRemaining,
        label: "Answering Time",
        type: "answering",
      };
    }
    return null;
  };

  const currentTimer = getCurrentTimer();

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#F1FFFA",
      }}
    >
      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 0,
          borderBottom: "1px solid #A9A5A5",
          bgcolor: "#F1FFFA",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <QuestionAnswer sx={{ color: "#4F46BA", fontSize: 28 }} />
            <Box>
              <Typography
                variant="overline"
                sx={{
                  lineHeight: 1,
                  textTransform: "capitalize",
                  color: "#A9A5A5",
                }}
              >
                {roundName} Round
              </Typography>
              <Typography
                variant="h6"
                sx={{ color: "#4F46BA", fontWeight: 700, lineHeight: 1.2 }}
              >
                Question {questionNumber}
              </Typography>
            </Box>
          </Stack>

          {/* Timer Display */}
          <Box
            sx={{
              minWidth: 160,
              height: 80,
              display: "flex",
              alignItems: "center",
              transition: "all 0.3s ease-in-out",
            }}
          >
            {currentTimer && (
              <Card
                sx={{
                  bgcolor: "#F1FFFA",
                  border: `2px solid ${getTimerColor(currentTimer.time)}`,
                  borderRadius: 2,
                  width: "100%",
                  opacity: currentTimer ? 1 : 0,
                  transform: currentTimer ? "scale(1)" : "scale(0.9)",
                  transition: "all 0.3s ease-in-out",
                }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Timer
                      sx={{
                        color: getTimerColor(currentTimer.time),
                        fontSize: 20,
                      }}
                    />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#A9A5A5",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                        }}
                      >
                        {currentTimer.label}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          color: getTimerColor(currentTimer.time),
                          fontWeight: 700,
                          fontFamily: "monospace",
                          lineHeight: 1,
                        }}
                      >
                        {formatTime(currentTimer.time)}
                        {isQuestionPanelTranscribing && (
                          <Chip
                            label="PAUSED"
                            size="small"
                            sx={{
                              ml: 1,
                              bgcolor: "#A9A5A5",
                              color: "#F1FFFA",
                              fontSize: "0.6rem",
                              height: 16,
                            }}
                          />
                        )}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Box>
        </Stack>
      </Paper>
      <Box
        sx={{
          flex: 1,
          p: { xs: 2, md: 3 },
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Stack
          spacing={3}
          sx={{
            maxWidth: "900px",
            mx: "auto",
            width: "100%",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Card sx={{ bgcolor: "#F1FFFA", border: "1px solid #A9A5A5" }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Typography
                variant="h5"
                sx={{ color: "#000000", fontWeight: 500, lineHeight: 1.5 }}
              >
                {questionText}
              </Typography>
            </CardContent>
          </Card>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              position: "relative",
              minHeight: "200px",
            }}
          >
            {copyPasteAlert && (
              <Alert
                severity="warning"
                sx={{
                  mb: 2,
                  bgcolor: "#F1FFFA",
                  color: "#A9A5A5",
                  "& .MuiAlert-icon": { color: "#A9A5A5" },
                }}
              >
                Copying and pasting are disabled for this field.
              </Alert>
            )}
            <TextField
              label="Your Answer"
              multiline
              rows={6}
              value={answerState}
              onChange={(e) => setAnswer(e.target.value)}
              onCopy={preventAction}
              onPaste={preventAction}
              placeholder={
                userMicEnabled
                  ? "Type your answer, or use the microphone to record."
                  : "Please wait for the interviewer to finish or for your answering time to begin."
              }
              variant="outlined"
              fullWidth
              disabled={isSubmitting || isTranscribing || !userMicEnabled}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#F1FFFA",
                  "& fieldset": {
                    borderColor: "#A9A5A5",
                  },
                  "&:hover fieldset": {
                    borderColor: "#4F46BA",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#4F46BA",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#A9A5A5",
                  "&.Mui-focused": {
                    color: "#4F46BA",
                  },
                },
                "& .MuiInputBase-input": {
                  color: "#000000",
                },
              }}
              InputProps={{ sx: { height: "100%", alignItems: "flex-start" } }}
            />
            <Tooltip
              title={
                isTranscribing
                  ? "Processing Audio..."
                  : isRecording
                    ? "Stop Recording"
                    : userMicEnabled
                      ? "Start Recording"
                      : "Microphone Disabled"
              }
            >
              <span>
                <IconButton
                  onClick={handleMicClick}
                  disabled={micButtonDisabled}
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    right: 16,
                    bgcolor: isRecording ? "#D92626" : "#4F46BA",
                    color: "#F1FFFA",
                    "&:hover": {
                      bgcolor: isRecording ? "#B91C1C" : "#3730A3",
                    },
                    "&:disabled": {
                      bgcolor: "#A9A5A5",
                      color: "#F1FFFA",
                    },
                  }}
                >
                  {isTranscribing ? (
                    <HourglassEmpty />
                  ) : isRecording ? (
                    <Stop />
                  ) : (
                    <Mic />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {micError && (
            <Alert
              severity="error"
              sx={{
                bgcolor: "#F1FFFA",
                color: "#D92626",
                "& .MuiAlert-icon": { color: "#D92626" },
              }}
            >
              {micError}
            </Alert>
          )}
          {error && (
            <Alert
              severity="warning"
              sx={{
                bgcolor: "#F1FFFA",
                color: "#A9A5A5",
                "& .MuiAlert-icon": { color: "#A9A5A5" },
              }}
            >
              {error}
            </Alert>
          )}
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={isDisabled}
            startIcon={
              isSubmitting ? (
                <CircularProgress size={20} sx={{ color: "#F1FFFA" }} />
              ) : (
                <Send />
              )
            }
            sx={{
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              bgcolor: "#4F46BA",
              color: "#F1FFFA",
              "&:hover": { bgcolor: "#3730A3" },
              "&:disabled": { bgcolor: "#A9A5A5", color: "#F1FFFA" },
            }}
          >
            {isSubmitting
              ? "Submitting..."
              : isRecording
                ? "Stop Recording to Submit"
                : isTranscribing
                  ? "Processing..."
                  : "Submit Answer & Continue"}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default QuestionPanel;
